import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import {
	forceSimulation,
	forceLink,
	forceManyBody,
	forceCenter,
	Simulation,
	SimulationLinkDatum,
	SimulationNodeDatum
} from "d3-force";
import styles from "./GroupHierarchyGraph.module.less";
import classNames from "classnames";
import {
	ensureNotNil,
	isNil,
	isNotNil,
	setTrackingId,
	throttle
} from "client/Misc/utils";
import {
	PanZoomState,
	panZoomToTransformValue,
	usePanZoom,
	useResizeObserver,
	useSynchronizedRef
} from "client/Hooks";
import { SizeInformation } from "client_legacy/ndaw/sizing";
import { IconButton } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";

export type GroupHierarchyGraphProps = {
	groups: readonly UserGroupWithRelationsModel[];
	selectedGroupId: number | null;
	onChangeSelectedGroupId: (id: number) => void;
	trackingId: string;
};

export type GroupHierarchyGraphHandle = {
	focusOn: (groupId: number) => void;
};

export const GroupHierarchyGraph = forwardRef(
	(props: GroupHierarchyGraphProps, ref: Ref<GroupHierarchyGraphHandle>): ReactElement => {
		const {
			selectedGroupId,
			onChangeSelectedGroupId,
			groups,
			trackingId
		} = props;

		const [areNamesVisible, setAreNamesVisible] = useState<boolean>(true);
		const [hoveredGroupId, setHoveredGroupId] = useState<number | null>(null);

		const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

		const { width, height } = useResizeObserver(rootRef);

		const [simulationData, setSimulationData] = useState<SimulationData>({
			nodes: [],
			links: []
		});

		const simulationDataRef: ReadonlyRefObject<SimulationData> = useSynchronizedRef(simulationData);

		const [panZoomState, setPanZoomState] = useState<PanZoomState>({
			scale: DEFAULT_SCALE,
			translateX: 0,
			translateY: 0
		});

		const { zoom: zoomHandler } = usePanZoom(
			setPanZoomState,
			rootRef,
			{
				minScale: MIN_SCALE,
				maxScale: MAX_SCALE
			}
		);

		const [selectedGroupMembers, selectedGroupMemberOf] = useMemo(
			(): [members: ReadonlySet<number>, memberOf: ReadonlySet<number>] => {
				const members = new Set<number>();
				const memberOf = new Set<number>();

				if (isNotNil(selectedGroupId)) {
					for (const group of groups) {
						if (group.id === selectedGroupId) {
							for (const member of group.members) {
								members.add(member);
							}
						} else {
							if (group.members.includes(selectedGroupId)) {
								memberOf.add(group.id);
							}
						}
					}
				}

				return [members, memberOf];
			},
			[groups, selectedGroupId]
		);

		const selectedGroupIdRef: ReadonlyRefObject<number | null> = useSynchronizedRef(selectedGroupId);

		useEffect(
			(): (() => void) => {
				const links: Link[] = [];

				const groupsWithRelations = new Set<number>();

				let groupToFollow: { id: number; amountOfMembers: number; } | undefined = undefined;

				for (const group of groups) {
					if (group.members.length > 0) {
						groupsWithRelations.add(group.id);

						if (groupToFollow === undefined
							|| groupToFollow.amountOfMembers < group.members.length
						) {
							groupToFollow = {
								id: group.id,
								amountOfMembers: group.members
									.length
							};
						}
					}

					for (const member of group.members) {
						groupsWithRelations.add(member);

						links.push({
							source: member,
							target: group.id
						});
					}
				}

				const nodes: Node[] = groups
					.filter((x) => groupsWithRelations.has(x.id))
					.map(
						(x): Node => ({ ...x })
					);

				let lastAutomaticallyPanZoomState: PanZoomState = {
					scale: DEFAULT_SCALE,
					translateX: 0,
					translateY: 0
				};

				setPanZoomState(lastAutomaticallyPanZoomState);

				const repaint = throttle(
					(): void => {
						const simulationData: SimulationData = {
							nodes: nodes.map(
								(x): Node => ({ ...x })
							),
							links: links.map(
								(x): Link => ({
									...x,
									// NOTE: this will make it so that the references don't point to the items in the nodes array, however we tend to only care
									// for the coordinates associated with those nodes anyway
									target: { ...x.target as Node },
									source: { ...x.source as Node }
								})
							)
						};

						setSimulationData(simulationData);

						const { current: host } = rootRef;

						const groupIdToFollow: number | undefined = selectedGroupIdRef.current
							?? groupToFollow?.id;

						if (!host || isNil(groupIdToFollow)) {
							return;
						}

						const node: Node | undefined = simulationData
							.nodes
							.find((x) => x.id === groupIdToFollow);

						if (!node) {
							return;
						}

						const { width, height } = host.getBoundingClientRect();

						setPanZoomState(
							(x) => {
								const didUserInteract: boolean = x !== lastAutomaticallyPanZoomState;
								if (didUserInteract) {
									return x;
								}

								lastAutomaticallyPanZoomState = {
									scale: DEFAULT_SCALE,
									translateX: (-(node.x ?? 0) * DEFAULT_SCALE) + (width / 2),
									translateY: (-(node.y ?? 0) * DEFAULT_SCALE) + (height / 2)
								};

								return lastAutomaticallyPanZoomState;
							}
						);
					},
					100
				);

				const host: HTMLDivElement = ensureNotNil(rootRef.current);
				const { width, height } = host.getBoundingClientRect();
				const dimension: SizeInformation = { width, height };

				const simulation: Simulation<Node, Link> = forceSimulation<Node>(nodes)
					.force(
						"link",
						forceLink<Node, Link>(links)
							.id(d => d.id)
					)
					.force("charge", forceManyBody())
					.force(
						"center",
						forceCenter(
							(dimension.width / 2) / DEFAULT_SCALE,
							(dimension.height / 2) / DEFAULT_SCALE
						)
					)
					.on("tick", repaint);

				const timeoutHandle: SetTimeoutHandle = setTimeout(
					(): void => {
						simulation.stop();
					},
					20_000
				);

				return (): void => {
					clearTimeout(timeoutHandle);
					simulation.stop();
				};
			},
			[groups, selectedGroupIdRef]
		);

		const focusOn = useCallback(
			(groupId: number): void => {
				const node: Node | undefined = simulationDataRef.current
					.nodes
					.find((x) => x.id === groupId);

				if (!node) {
					console.warn(`Node '${groupId}' could not be found`);
					return;
				}

				const { current: host } = rootRef;

				if (!host) {
					return;
				}

				const { width, height } = host.getBoundingClientRect();

				setPanZoomState({
					scale: DEFAULT_SCALE,
					translateX: (-(node.x ?? 0) * DEFAULT_SCALE) + (width / 2),
					translateY: (-(node.y ?? 0) * DEFAULT_SCALE) + (height / 2)
				});
			},
			[simulationDataRef]
		);

		useImperativeHandle(
			ref,
			(): GroupHierarchyGraphHandle => ({
				focusOn
			}),
			[focusOn]
		);

		const { nodes, links } = simulationData;

		return (
			<div
				{...setTrackingId(trackingId)}
				className={styles.root}
				ref={rootRef}
			>
				<GraphTools
					trackingId="graph-tools"
					onZoom={zoomHandler}
					areNamesVisible={areNamesVisible}
					onChangeAreNamesVisible={setAreNamesVisible}
				/>

				<svg
					{...setTrackingId("image")}
					xmlns="http://www.w3.org/2000/svg"
					viewBox={`0 0 ${width} ${height}`}
					className={styles.svg}
					preserveAspectRatio="none"
				>
					<defs>
						<marker
							id="arrow-normal"
							viewBox="0 0 3.5 7"
							refX="1.5"
							refY="3.5"
							markerWidth="1.25"
							markerHeight="2.5"
							markerUnits="userSpaceOnUse"
							orient="auto"
							className={classNames(styles.marker, styles.marker_normal)}
						>
							<polyline
								points="0,0 3,3.5 0,7"
							/>
						</marker>

						<marker
							id="arrow-highlight"
							viewBox="0 0 3.5 7"
							refX="1.5"
							refY="3.5"
							markerWidth="2.5"
							markerHeight="5"
							markerUnits="userSpaceOnUse"
							orient="auto"
							className={classNames(styles.marker, styles.marker_highlight)}
						>
							<polyline
								points="0,0 3,3.5 0,7"
							/>
						</marker>
					</defs>

					<g
						name="zoom-group"
						transform={panZoomToTransformValue(panZoomState)}
					>
						<g
							name="link-group"
						>
							{links.map((x): ReactElement => {
								// The simulation makes the source & target into nodes
								const source = x.source as Node;
								const target = x.target as Node;

								const midwayX: number = ((source.x ?? 0) + (target.x ?? 0)) / 2;
								const midwayY: number = ((source.y ?? 0) + (target.y ?? 0)) / 2;

								const isHighlighted: boolean = selectedGroupId !== null
									&& (
										source.id === selectedGroupId
										|| target.id === selectedGroupId
									);

								// isHighlighted should not be part of the key, however in safari it appears
								// that updating the marker-mid attribute does not result in a visual change
								// (i.e. the marker getting changed). So we put the marker differentiator (isHighlighted)
								// into the key, so it will create in a new element instead of updating the existing element
								// when the marker changes
								const key: string = `${source.id}_${target.id}_${isHighlighted}`;

								return (
									<polyline
										key={key}
										strokeWidth={1}
										points={`${source.x},${source.y} ${midwayX},${midwayY} ${target.x},${target.y}`}
										markerMid={`url(#${isHighlighted ? "arrow-highlight" : "arrow-normal"})`}
										className={classNames(
											styles.line,
											{ [styles.line_highlight]: isHighlighted }
										)}
									/>
								);
							})}
						</g>

						<g
							name="node-group"
						>
							{nodes.map((x): ReactElement => {
								const isHovered: boolean = x.id === hoveredGroupId;
								const isSelected: boolean = x.id === selectedGroupId;
								const isChildOfSelected: boolean = selectedGroupMembers.has(x.id);
								const isParentOfSelected: boolean = selectedGroupMemberOf.has(x.id);

								return (
									<g
										key={x.id}
										onMouseEnter={() => setHoveredGroupId(x.id)}
										onMouseLeave={() => setHoveredGroupId(null)}
										onClick={() => onChangeSelectedGroupId(x.id)}
										className={styles.nodeRect}
									>
										<circle
											cx={x.x}
											cy={x.y}
											r={isHovered || isSelected ? 3 : 2}
											className={classNames(
												styles.node,
												{
													[styles.node_selected]: isSelected,
													[styles.node_child]: isChildOfSelected,
													[styles.node_parent]: isParentOfSelected
												}
											)}
										/>

										<text
											x={x.x}
											y={x.y}
											className={classNames(
												styles.text,
												{
													[styles.text_selected]: isSelected,
													[styles.text_hovered]: isHovered,
													[styles.text_ofInterests]: isSelected
														|| isChildOfSelected
														|| isParentOfSelected,
													[styles.text_hidden]: !areNamesVisible
												}
											)}
										>
											{x.name}
										</text>
									</g>
								);
							})}
						</g>
					</g>
				</svg>
			</div>
		);
	}
);
GroupHierarchyGraph.displayName = "GroupHierarchyGraph";

export type UserGroupWithRelationsModel = {
	id: number;
	name: string;
	members: readonly number[];
};

type Node = UserGroupWithRelationsModel & SimulationNodeDatum;
type Link = SimulationLinkDatum<Node>;

type SimulationData = {
	nodes: readonly Node[];
	links: readonly Link[];
};

// The main difficulty is the force simulation, i.e. to get the right ratio of link length vs node size.
// Messing with the "distance" of links results in that the nodes are not really positioned correctly.
// So instead we rely on the default dinstance implementation, we make all visual elements based on that link
// length, and then use a different default scale to make it appear somewhat visible in the UI
const DEFAULT_SCALE = 3;
const MIN_SCALE = DEFAULT_SCALE / 4;
const MAX_SCALE = DEFAULT_SCALE * 4;

type GraphToolsProps = {
	onZoom: (action: "in" | "out") => void;
	areNamesVisible: boolean;
	onChangeAreNamesVisible: (value: boolean) => void;
	trackingId: string;
};

const GraphTools = (props: GraphToolsProps): ReactElement => {
	const {
		onZoom,
		areNamesVisible,
		onChangeAreNamesVisible,
		trackingId,
	} = props;

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const pointerDownHandler = useCallback(
		(args: PointerEvent): void => {
			args.stopPropagation();
		},
		[]
	);

	// Native listener since usePanZoom uses native listeners as well, and the react Synthetic
	// events come after the native events, so if you want to prevent bubbling to prevent a native
	// listener from being called, you need to use native events as well
	useEffect(
		(): (() => void) | void => {
			const { current: element } = ref;
			if (!element) {
				return;
			}

			element.addEventListener("pointerdown", pointerDownHandler);

			return (): void => {
				element.removeEventListener("pointerdown", pointerDownHandler);
			};
		},
		[pointerDownHandler]
	);

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.tools}
			ref={ref}
		>
			<IconButton
				icon="plus"
				onClick={() => onZoom("in")}
				trackingId="zoom-in"
				kind="secondary"
				shape="round"
				tooltip={localize("_ZoomIn")}
			/>

			<IconButton
				icon="minus"
				onClick={() => onZoom("out")}
				trackingId="zoomout"
				kind="secondary"
				shape="round"
				tooltip={localize("_ZoomOut")}
			/>

			<IconButton
				icon={areNamesVisible ? "eye-slash" : "eye"}
				onClick={() => onChangeAreNamesVisible(!areNamesVisible)}
				trackingId={areNamesVisible ? "hide-name" : "show-name"}
				kind="secondary"
				shape="round"
				tooltip={localize(
					areNamesVisible
						? "_HideGroupNames"
						: "_ShowGroupNames"
				)}
			/>
		</div>
	);
};
GraphTools.displayName = "GraphTools";
